import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Takes in a list of class values and merges them into a single class string (Note will remove duplicates or conflicting classes)
 * @param {ClassValue[]} inputs - the list of class values to merge
 * @returns {string} the merged class string
 * @example cl('h-full', {'border': true, 'border-red': isErrorBorder })
 * @example cl('h-full', isFullWidth && 'w-full') // 'h-full w-full' || 'h-full'
 * @example cl('h-full', 'h-full') // 'h-full'
 * @example cl('px-2 py-1 bg-red hover:bg-dark-red', 'p-3 bg-[#B91C1C]') // → 'hover:bg-dark-red p-3 bg-[#B91C1C]'
 *
 */
export const tw = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

/**
 * Normalizes various tier/rarity strings into a standardized set of tiers: 'essential', 'exclusive', 'grail', or 'iconic'
 * @param {string | undefined} tier - The input tier string to be cleaned and normalized
 * @returns {string} One of the following standardized tiers: 'essential', 'exclusive', 'grail', or 'iconic'
 *
 * Mapping:
 * - essential: 'very common', 'essential', 'utility'
 * - exclusive: 'core', 'exclusive', 'unique', 'common'
 * - grail: 'grail', 'rare'
 * - iconic: '1:1', 'iconic', 'limited', 'custom'
 */
export const getCleanedTier = (tier: string | undefined) => {
  const cleanedTier = tier?.toLowerCase().trim();

  switch (cleanedTier) {
    case 'very common':
      return 'essential';
    case 'essential':
      return 'essential';
    case 'utility':
      return 'essential';
    case 'core':
      return 'exclusive';
    case 'exclusive':
      return 'exclusive';
    case 'unique':
      return 'exclusive';
    case 'common':
      return 'exclusive';
    case 'grail':
      return 'grail';
    case 'rare':
      return 'grail';
    case '1:1':
      return 'iconic';
    case 'iconic':
      return 'iconic';
    case 'limited':
      return 'iconic';
    case 'custom':
      return 'iconic';
    default:
      return 'essential';
  }
};

/**
 * Returns the appropriate Tailwind background class based on the rarity tier
 * @param {string | undefined} rarity - The rarity tier to convert to a background class
 * @returns {string} Tailwind background class corresponding to the rarity tier
 *
 * Background mappings:
 * - exclusive → bg-exclusive
 * - grail → bg-grail
 * - iconic → bg-one-of-one
 * - essential/default → bg-white
 */
export const getBackgroundRarityStyle = (
  rarity: string | undefined,
): string => {
  const cleanedRarity = getCleanedTier(rarity);

  switch (cleanedRarity) {
    case 'exclusive':
      return 'bg-exclusive';
    case 'grail':
      return 'bg-grail';
    case 'iconic':
      return 'bg-one-of-one';
    case 'essential':
      return 'bg-white';
    default:
      return 'bg-white';
  }
};

/**
 * Returns the appropriate Tailwind text styling class based on the rarity tier
 * @param {string} rarity - The rarity tier to convert to a text styling class
 * @returns {string} Tailwind text styling class corresponding to the rarity tier
 *
 * Text style mappings:
 * - exclusive → blue text (#7DAFFF)
 * - grail → gradient text with bg-grail
 * - iconic → gradient text with bg-one-of-one
 * - essential/default → mushroom-300 colored text
 */
export const getTextRarityClass = (rarity: string): string => {
  const cleanedRarity = getCleanedTier(rarity);

  switch (cleanedRarity) {
    case 'exclusive':
      return 'text-[#7DAFFF]';
    case 'grail':
      return 'bg-grail bg-clip-text text-transparent';
    case 'iconic':
      return 'bg-one-of-one bg-clip-text text-transparent';
    case 'essential':
      return 'text-mushroom-300';
    default:
      return 'text-mushroom-300';
  }
};
